// Bilingual (zh default, en) i18n for the viewer. Language is remembered in
// localStorage ("maitv.lang"); first-time visitors get Chinese. English strings
// intentionally mirror the original UI copy so the en locale stays test-stable.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const SUPPORTED_LANGS = ["zh", "en"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

const zh = {
  common: { loading: "加载中…" },
  lang: { label: "语言", zh: "中文", en: "English" },
  nav: {
    goLive: "开播",
    login: "登录",
    profile: "个人资料",
    creatorStudio: "创作者工作室",
    logout: "退出登录",
  },
  home: {
    title: "现在直播",
    liveCount: "{{count}} 直播中",
    emptyTitle: "这里什么都没有",
    emptyHint: "稍后再来看看吧。",
  },
  grid: { loadError: "加载失败，请稍后重试。", emptyTitle: "暂时没有直播", emptyHint: "稍后再来看看。" },
  status: {
    live: "直播中",
    scheduled: "预约",
    waiting: "等待中",
    paused: "暂停",
    offline: "离线",
    ended: "已结束",
  },
  stream: {
    notFound: "未找到直播",
    notFoundHint: "该直播可能是私密的、已被移除，或链接有误。",
    video: "视频",
    chat: "聊天",
  },
  player: {
    paused: "主播连接中断 —— 正在等待重连…",
    offline: "直播离线",
    unavailable: "直播暂时无法播放",
    retry: "重试",
    unmute: "点击取消静音",
    unmuteTitle: "取消静音",
    llOn: "低延迟 开",
    llOff: "低延迟 关",
    llTitle: "低延迟开关",
    scheduledBadge: "预定直播",
    startingSoon: "即将开始",
    ended: "直播已结束。",
    playPause: "播放 / 暂停",
    mute: "静音",
    volume: "音量",
    fullscreen: "全屏",
    pip: "画中画",
    goLive: "回到直播",
    seek: "进度",
  },
  danmaku: {
    on: "弹幕已开",
    off: "弹幕已关",
    settings: "弹幕设置",
    placeholder: "发个弹幕…",
    send: "发送",
    loginToSend: "登录后发弹幕",
    opacity: "不透明度",
    area: "显示区域",
    areaQuarter: "顶部",
    areaHalf: "半屏",
    areaFull: "全屏",
    speed: "速度",
    speedSlow: "慢",
    speedNormal: "正常",
    speedFast: "快",
    fontSize: "字号",
    fontSmall: "小",
    fontNormal: "标准",
    fontLarge: "大",
  },
  chat: {
    title: "聊天",
    connecting: "连接中…",
    connected: "已连接",
    disconnected: "已断开",
    empty: "还没有消息，发条评论吧。",
    placeholder: "说点什么…",
    send: "发送",
    loginToComment: "登录后参与聊天",
    streamer: "主播",
    anon: "匿名",
    system: "系统",
  },
  info: {
    uptime: "已直播时长",
    record: "录制",
    download: "下载",
    share: "分享",
    copied: "已复制",
    showMore: "展开",
    showLess: "收起",
    viewers: "{{count}} 人观看",
  },
  create: {
    title: "创建直播",
    created: "直播已创建",
    keyOnce: "请立即复制推流密钥 —— 它不会再次显示。",
    streamKey: "推流密钥 Stream Key",
    obs: "OBS / 推流地址",
    goToStream: "前往直播间",
    fieldTitle: "标题",
    titlePlaceholder: "深夜直播",
    slug: "短链接（可选）",
    description: "简介（可选）",
    schedule: "预定开始时间（可选）",
    record: "录制本场直播",
    cancel: "取消",
    create: "创建",
    creating: "创建中…",
    copy: "复制",
    copied: "已复制",
    errNoPublish: "你没有开播权限。",
    errNoRecord: "你没有录制权限。",
    errGeneric: "创建直播失败。",
  },
  login: {
    title: "登录 {{site}}",
    subtitle: "登录会自动为你创建账号。",
    github: "使用 GitHub 继续",
    google: "使用 Google 继续",
    passkey: "使用 Passkey",
    passkeyWaiting: "等待 Passkey…",
    telegram: "Telegram 登录请在 Telegram 机器人中完成。",
    errStart: "无法开始 Passkey 登录。",
    errFail: "Passkey 登录失败。",
  },
  profile: {
    title: "个人资料",
    notSignedIn: "你尚未登录",
    notSignedInHint: "登录后即可管理个人资料。",
    changeAvatar: "更换头像",
    uploading: "上传中…",
    displayName: "显示名称",
    save: "保存",
    saving: "保存中…",
    saved: "已保存",
    loginMethods: "登录方式",
    addPasskey: "添加 Passkey",
    waiting: "等待…",
    unlink: "解绑",
    keepOne: "至少需要保留一种登录方式",
    noMethods: "暂无已绑定的登录方式。",
    passkeyFail: "Passkey 注册失败。",
  },
  studio: {
    title: "创作者工作室",
    subtitle: "管理你的直播、查看实时状态与录像。",
    goLive: "开播",
    myStreams: "我的直播",
    manage: "管理",
    emptyAll: "还没有直播",
    emptyHint: "点击「开播」创建你的第一场直播。",
    ago: "{{time}} 前",
    featured: "精选",
    loginTitle: "登录后进入工作室",
    loginHint: "创作者工作室用于管理你的直播。",
    noPublishTitle: "你还没有开播权限",
    noPublishHint: "请联系管理员为你的账号授予 PUBLISH 权限后再使用工作室。",
  },
  manage: {
    fieldTitle: "标题",
    slug: "短链接",
    slugPlaceholder: "（可选）",
    description: "简介",
    schedule: "预定开始时间",
    record: "录制本场直播",
    statsTitle: "本场数据",
    sViewers: "当前观看",
    sUptime: "直播时长",
    sStarted: "开播时间",
    sRecordings: "录像数",
    sRegion: "节点",
    notStarted: "未开播",
    streamKey: "推流密钥",
    keyOnce: "请立即复制 —— 仅显示这一次。",
    resetKey: "重置密钥",
    resetHint: "重置后，旧密钥立即失效。",
    recordings: "录像（{{count}}）",
    downloadLatest: "下载最新录像",
    watch: "查看",
    endStream: "结束直播",
    endConfirmTitle: "结束这场直播？",
    endConfirmDesc: "直播将立即结束，观众会断开连接。此操作无法撤销。",
    cancel: "取消",
    save: "保存修改",
    saved: "已保存",
    saveFailed: "保存失败",
    ended: "直播已结束",
    endFailed: "结束失败",
    resetFailed: "重置失败",
  },
  studioStream: {
    back: "返回工作室",
    viewPublic: "查看直播页",
    detailsTitle: "直播信息",
    dangerTitle: "危险操作",
    notFound: "未找到直播",
    notFoundHint: "该直播不存在，或你无权管理它。",
  },
  ingest: {
    title: "推流地址",
    server: "服务器地址（Server）",
    key: "推流密钥（Stream Key）",
    streamId: "推流 ID（Stream ID）",
    empty: "暂无可用的推流节点。",
    keyHidden:
      "出于安全考虑，自建节点的推流密钥不会再次显示。请使用创建或上次重置时保存的密钥，或在下方「重置密钥」获取新密钥。",
  },
  notConfigured: {
    title: "{{site}} 尚未完成配置",
    hint1: "管理员需要先完成初始化，直播才能上线。",
    hint2: "请打开管理后台并完成设置向导。",
  },
};

const en: typeof zh = {
  common: { loading: "Loading…" },
  lang: { label: "Language", zh: "中文", en: "English" },
  nav: {
    goLive: "Go Live",
    login: "Log in",
    profile: "Profile",
    creatorStudio: "Creator Studio",
    logout: "Log out",
  },
  home: {
    title: "Live Now",
    liveCount: "{{count}} live",
    emptyTitle: "Nothing here yet",
    emptyHint: "Check back later.",
  },
  grid: { loadError: "Couldn't load. Please try again.", emptyTitle: "No streams right now", emptyHint: "Check back soon." },
  status: {
    live: "LIVE",
    scheduled: "SCHEDULED",
    waiting: "WAITING",
    paused: "PAUSED",
    offline: "OFFLINE",
    ended: "ENDED",
  },
  stream: {
    notFound: "Stream not found",
    notFoundHint: "This stream may be private, removed, or the link is wrong.",
    video: "Video",
    chat: "Chat",
  },
  player: {
    paused: "Streamer connection lost — waiting to reconnect…",
    offline: "Stream offline",
    unavailable: "Stream unavailable",
    retry: "Retry",
    unmute: "Tap to unmute",
    unmuteTitle: "Unmute",
    llOn: "LL on",
    llOff: "LL off",
    llTitle: "Low-latency toggle",
    scheduledBadge: "Scheduled",
    startingSoon: "Starting soon",
    ended: "Stream ended.",
    playPause: "Play / Pause",
    mute: "Mute",
    volume: "Volume",
    fullscreen: "Fullscreen",
    pip: "Picture in picture",
    goLive: "Go to live",
    seek: "Seek",
  },
  danmaku: {
    on: "Danmaku on",
    off: "Danmaku off",
    settings: "Danmaku settings",
    placeholder: "Send a danmaku…",
    send: "Send",
    loginToSend: "Log in to send danmaku",
    opacity: "Opacity",
    area: "Display area",
    areaQuarter: "Top",
    areaHalf: "Half",
    areaFull: "Full",
    speed: "Speed",
    speedSlow: "Slow",
    speedNormal: "Normal",
    speedFast: "Fast",
    fontSize: "Font size",
    fontSmall: "Small",
    fontNormal: "Normal",
    fontLarge: "Large",
  },
  chat: {
    title: "Chat",
    connecting: "Connecting…",
    connected: "Connected",
    disconnected: "Disconnected",
    empty: "No messages yet. Be the first to comment.",
    placeholder: "Say something…",
    send: "Send",
    loginToComment: "Log in to comment",
    streamer: "streamer",
    anon: "anon",
    system: "system",
  },
  info: {
    uptime: "Uptime",
    record: "Record",
    download: "Download",
    share: "Share",
    copied: "Copied",
    showMore: "Show more",
    showLess: "Show less",
    viewers: "{{count}} viewers",
  },
  create: {
    title: "Create a stream",
    created: "Stream created",
    keyOnce: "Copy your stream key now — it won't be shown again.",
    streamKey: "Stream key",
    obs: "OBS / ingest",
    goToStream: "Go to stream",
    fieldTitle: "Title",
    titlePlaceholder: "Late-night session",
    slug: "Slug (optional)",
    description: "Description (optional)",
    schedule: "Scheduled start (optional)",
    record: "Record this stream",
    cancel: "Cancel",
    create: "Create",
    creating: "Creating…",
    copy: "Copy",
    copied: "Copied",
    errNoPublish: "You don't have permission to publish streams.",
    errNoRecord: "You don't have permission to enable recording.",
    errGeneric: "Could not create the stream.",
  },
  login: {
    title: "Sign in to {{site}}",
    subtitle: "Signing in creates your account automatically.",
    github: "Continue with GitHub",
    google: "Continue with Google",
    passkey: "Use a passkey",
    passkeyWaiting: "Waiting for passkey…",
    telegram: "Telegram sign-in is available from the Telegram bot.",
    errStart: "Could not start passkey sign-in.",
    errFail: "Passkey sign-in failed.",
  },
  profile: {
    title: "Profile",
    notSignedIn: "You're not signed in",
    notSignedInHint: "Sign in to manage your profile.",
    changeAvatar: "Change avatar",
    uploading: "Uploading…",
    displayName: "Display name",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    loginMethods: "Linked sign-in methods",
    addPasskey: "Add passkey",
    waiting: "Waiting…",
    unlink: "Unlink",
    keepOne: "You must keep at least one sign-in method",
    noMethods: "No linked sign-in methods.",
    passkeyFail: "Passkey registration failed.",
  },
  studio: {
    title: "Creator Studio",
    subtitle: "Manage your streams, monitor live status and recordings.",
    goLive: "Go Live",
    myStreams: "My Streams",
    manage: "Manage",
    emptyAll: "No streams yet",
    emptyHint: "Click “Go Live” to create your first stream.",
    ago: "{{time}} ago",
    featured: "Featured",
    loginTitle: "Sign in to open the Studio",
    loginHint: "The Creator Studio is where you manage your streams.",
    noPublishTitle: "You don't have publish access yet",
    noPublishHint: "Ask an admin to grant your account PUBLISH permission to use the Studio.",
  },
  manage: {
    fieldTitle: "Title",
    slug: "Slug",
    slugPlaceholder: "(optional)",
    description: "Description",
    schedule: "Scheduled start",
    record: "Record this stream",
    statsTitle: "Stream stats",
    sViewers: "Viewers now",
    sUptime: "Uptime",
    sStarted: "Started",
    sRecordings: "Recordings",
    sRegion: "Node",
    notStarted: "Not started",
    streamKey: "Stream key",
    keyOnce: "Copy it now — shown only once.",
    resetKey: "Reset key",
    resetHint: "Resetting invalidates the old key immediately.",
    recordings: "Recordings ({{count}})",
    downloadLatest: "Download latest recording",
    watch: "View",
    endStream: "End stream",
    endConfirmTitle: "End this stream?",
    endConfirmDesc: "The stream ends immediately and viewers disconnect. This can't be undone.",
    cancel: "Cancel",
    save: "Save changes",
    saved: "Saved",
    saveFailed: "Save failed",
    ended: "Stream ended",
    endFailed: "Couldn't end the stream",
    resetFailed: "Couldn't reset the key",
  },
  studioStream: {
    back: "Back to Studio",
    viewPublic: "View public page",
    detailsTitle: "Stream details",
    dangerTitle: "Danger zone",
    notFound: "Stream not found",
    notFoundHint: "This stream doesn't exist, or you don't have permission to manage it.",
  },
  ingest: {
    title: "Stream connection",
    server: "Server URL",
    key: "Stream key",
    streamId: "Stream ID",
    empty: "No ingest endpoints available.",
    keyHidden:
      "For security, the stream key for self-hosted nodes isn't shown again. Use the key you saved when you created or last reset the stream, or reset it below to get a new one.",
  },
  notConfigured: {
    title: "{{site}} is not yet configured",
    hint1: "An administrator needs to finish setup before streams can go live.",
    hint2: "Open the admin app and complete the setup wizard to get started.",
  },
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { zh: { translation: zh }, en: { translation: en } },
    fallbackLng: "zh", // first-time visitors (no stored pref) get Chinese
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true, // map en-US -> en, zh-CN -> zh
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "maitv.lang",
    },
  });

export default i18n;
